import { useState, useEffect, useCallback } from 'react';
import { Placeholder } from './types';

interface UseDragPlaceholderProps {
  placeholders: Placeholder[];
  scale: number;
  updatePlaceholder: (id: string, updates: Partial<Placeholder>) => void;
  selectPlaceholder: (id: string) => void;
}

export const useDragPlaceholder = ({
  placeholders,
  scale,
  updatePlaceholder,
  selectPlaceholder,
}: UseDragPlaceholderProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragPlaceholderId, setDragPlaceholderId] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const handlePlaceholderMouseDown = useCallback((e: React.MouseEvent, placeholderId: string) => {
    e.preventDefault();
    e.stopPropagation();

    const placeholder = placeholders.find(p => p.id === placeholderId);
    if (!placeholder) return;

    selectPlaceholder(placeholderId);
    setIsDragging(true);
    setDragPlaceholderId(placeholderId);
    setDragStart({ x: e.clientX, y: e.clientY });
    setDragOffset({ x: 0, y: 0 });
  }, [placeholders, selectPlaceholder]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !dragStart || !dragPlaceholderId) return;

      const dx = (e.clientX - dragStart.x) / scale;
      const dy = (e.clientY - dragStart.y) / scale;
      setDragOffset({ x: dx, y: -dy });
    };

    const handleMouseUp = () => {
      if (isDragging && dragPlaceholderId && (dragOffset.x !== 0 || dragOffset.y !== 0)) {
        const placeholder = placeholders.find(p => p.id === dragPlaceholderId);
        if (placeholder) {
          updatePlaceholder(dragPlaceholderId, {
            rect: {
              ...placeholder.rect,
              x: placeholder.rect.x + dragOffset.x,
              y: placeholder.rect.y + dragOffset.y,
            }
          });
        }
      }
      setIsDragging(false);
      setDragPlaceholderId(null);
      setDragStart(null);
      setDragOffset({ x: 0, y: 0 });
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, dragPlaceholderId, dragOffset, placeholders, scale, updatePlaceholder]);

  const getPlaceholderScreenPos = useCallback((
    p: Placeholder,
    pdfToScreen: (pdfX: number, pdfY: number, height: number) => { x: number; y: number }
  ) => {
    const isDraggingThis = isDragging && dragPlaceholderId === p.id;
    const offsetX = isDraggingThis ? dragOffset.x : 0;
    const offsetY = isDraggingThis ? dragOffset.y : 0;

    return pdfToScreen(
      p.rect.x + offsetX,
      p.rect.y + offsetY,
      p.rect.h
    );
  }, [isDragging, dragPlaceholderId, dragOffset]);

  return {
    isDragging,
    dragPlaceholderId,
    handlePlaceholderMouseDown,
    getPlaceholderScreenPos,
  };
};
